import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search, Bug, AlertTriangle, ExternalLink, Filter, ArrowLeft
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  getVulnerabilities,
  getSeverityColor,
  getSeverityBadgeColor
} from '@/services/litesabre';

const LiteSabreVulnerabilities = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine base path from current location
  const basePath = location.pathname.startsWith('/litesabre') && !location.pathname.startsWith('/litesabre-pro') 
    ? '/litesabre' 
    : '/grc/litesabre';
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');

  useEffect(() => {
    loadVulnerabilities();
  }, [severityFilter]);

  const loadVulnerabilities = async () => {
    try {
      setLoading(true);
      const params = {};
      if (severityFilter) params.severity = severityFilter;
      
      const data = await getVulnerabilities(params);
      setVulnerabilities(data);
    } catch (err) {
      console.error('Failed to load vulnerabilities:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredVulns = vulnerabilities.filter(v =>
    v.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.cve_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.package_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`${basePath}`)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Vulnerabilities</h1>
            <p className="text-gray-500">All unique vulnerabilities found across scans</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by CVE, package, or title..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <select
              className="border rounded-md px-3 py-2 text-sm"
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
            >
              <option value="">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Vulnerabilities Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-pulse space-y-2">
                <div className="h-8 bg-gray-200 rounded"></div>
                <div className="h-8 bg-gray-200 rounded"></div>
                <div className="h-8 bg-gray-200 rounded"></div>
              </div>
            </div>
          ) : filteredVulns.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Bug className="w-12 h-12 mx-auto text-gray-300 mb-2" />
              <p>No vulnerabilities found</p>
              <p className="text-sm">Run a scan to detect vulnerabilities</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-gray-500">Severity</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-500">CVE ID</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-500">Title</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-500">Package</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-500">Occurrences</th>
                    <th className="text-left p-4 text-sm font-medium text-gray-500">First Seen</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredVulns.map((vuln, idx) => (
                    <tr key={vuln._id || idx} className="hover:bg-gray-50">
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs rounded border ${getSeverityBadgeColor(vuln.severity)}`}>
                          {vuln.severity}
                        </span>
                      </td>
                      <td className="p-4">
                        {vuln.cve_id ? (
                          <a
                            href={`https://nvd.nist.gov/vuln/detail/${vuln.cve_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-sm text-indigo-600 hover:underline flex items-center gap-1"
                          >
                            {vuln.cve_id}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        <p className="font-medium text-sm max-w-xs truncate" title={vuln.title}>
                          {vuln.title}
                        </p>
                      </td>
                      <td className="p-4">
                        <span className="font-mono text-sm">{vuln.package_name}</span>
                      </td>
                      <td className="p-4">
                        <span className="px-2 py-1 bg-gray-100 rounded text-sm">
                          {vuln.occurrence_count}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-gray-500">
                        {vuln.first_seen ? new Date(vuln.first_seen).toLocaleDateString() : '-'}
                      </td>
                      <td className="p-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`${basePath}/vulnerabilities/${vuln._id}`)}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LiteSabreVulnerabilities;
